import { useEffect } from "react";
import { initTelegram } from "./telegram/init";
import { useGameStore } from "./store/gameStore";
import { useUIStore } from "./store/uiStore";
import { useApi } from "./hooks/useApi";
import { ResourceBar } from "./components/shared/ResourceBar";
import { BottomNav } from "./components/navigation/BottomNav";
import { CityScreen } from "./screens/CityScreen";
import { IslandScreen } from "./screens/IslandScreen";
import { ResearchScreen } from "./screens/ResearchScreen";

export function App() {
  const isLoading = useGameStore((s) => s.isLoading);
  const isAuthenticated = useGameStore((s) => s.isAuthenticated);
  const error = useGameStore((s) => s.error);
  const activeCityId = useGameStore((s) => s.activeCityId);
  const setAuth = useGameStore((s) => s.setAuth);
  const updateCity = useGameStore((s) => s.updateCity);
  const setError = useGameStore((s) => s.setError);
  const setLoading = useGameStore((s) => s.setLoading);
  const currentScreen = useUIStore((s) => s.currentScreen);
  const api = useApi();

  useEffect(() => {
    initTelegram();
    authenticate();
  }, []);

  // Poll city resources every 5 seconds once authenticated
  useEffect(() => {
    if (!isAuthenticated || !activeCityId) return;

    const refresh = async () => {
      try {
        const cityData = await api.getCity(activeCityId);
        updateCity(cityData);
      } catch {
        // Silent — don't disrupt gameplay on network blip
      }
    };

    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [isAuthenticated, activeCityId]);

  async function authenticate() {
    try {
      setLoading(true);
      const data = await api.auth();
      setAuth(data.user, data.cities, data.researches);
    } catch (e: any) {
      console.error("Auth failed:", e);
      setError(e.message ?? "Failed to connect");
      setLoading(false);
    }
  }

  // Loading state
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Error state
  if (error) {
    return <ErrorScreen message={error} onRetry={authenticate} />;
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <ErrorScreen message="Authentication required" onRetry={authenticate} />;
  }

  // Main game view
  return (
    <div style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      background: "var(--papyrus)",
    }}>
      <ResourceBar />
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {currentScreen === "city" && <CityScreen />}
        {currentScreen === "island" && <IslandScreen />}
        {currentScreen === "research" && <ResearchScreen />}
        {currentScreen === "military" && <PlaceholderScreen icon={"\u2694\uFE0F"} label="Military" />}
        {currentScreen === "trade" && <PlaceholderScreen icon={"\u2693"} label="Trade" />}
      </div>
      <BottomNav />
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "24px",
      background: "linear-gradient(170deg, var(--papyrus) 0%, var(--parchment) 50%, var(--papyrus-dark) 100%)",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Decorative background circles */}
      <div style={{
        position: "absolute",
        top: "-40%",
        right: "-30%",
        width: "80vw",
        height: "80vw",
        borderRadius: "50%",
        border: "1px solid rgba(201,169,97,0.12)",
      }} />
      <div style={{
        position: "absolute",
        bottom: "-30%",
        left: "-20%",
        width: "60vw",
        height: "60vw",
        borderRadius: "50%",
        border: "1px solid rgba(201,169,97,0.08)",
      }} />

      {/* Logo area */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        animation: "stagger-in 0.6s ease both",
      }}>
        <span style={{
          fontSize: "3.5rem",
          animation: "float 3s ease infinite",
        }}>
          {"\u{1F3DB}\uFE0F"}
        </span>
        <h1 style={{
          fontFamily: "var(--font-display)",
          fontSize: "1.8rem",
          fontWeight: 900,
          color: "var(--ink)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}>
          Ikarium
        </h1>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "0.85rem",
          color: "var(--ink-faded)",
          fontStyle: "italic",
        }}>
          Build your empire across the seas
        </p>
      </div>

      {/* Loading indicator */}
      <div style={{
        display: "flex",
        gap: "6px",
        animation: "stagger-in 0.6s ease 0.3s both",
      }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: "var(--gold)",
            animation: `pulse 1.2s ease infinite`,
            animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </div>
    </div>
  );
}

function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{
      height: "100dvh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "20px",
      padding: "32px",
      background: "var(--papyrus)",
    }}>
      <span style={{ fontSize: "2.5rem" }}>{"\u{1F3DA}\uFE0F"}</span>
      <h2 style={{
        fontFamily: "var(--font-display)",
        color: "var(--ink)",
        textAlign: "center",
      }}>
        Lost at Sea
      </h2>
      <p style={{
        color: "var(--ink-faded)",
        textAlign: "center",
        maxWidth: "280px",
        fontSize: "0.9rem",
      }}>
        {message}
      </p>
      <button className="btn-primary" onClick={onRetry}>
        Try Again
      </button>
    </div>
  );
}

function PlaceholderScreen({ icon, label }: { icon: string; label: string }) {
  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "var(--space-lg)",
    }}>
      <span style={{ fontSize: "3rem", opacity: 0.4 }}>{icon}</span>
      <span style={{
        fontFamily: "var(--font-display)",
        fontSize: "0.85rem",
        color: "var(--ink-faded)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      }}>
        {label} — Coming Soon
      </span>
    </div>
  );
}
